/**
 * RegistrarPerdaDialog — registra a perda do lead (acao do vendedor).
 *
 * Coleta o MOTIVO (lista canonica agrupada, espelha MOTIVOS_PERDA_LEAD do
 * backend) + um campo de DETALHE livre que aparece SEMPRE, independente do
 * motivo escolhido. O detalhe e obrigatorio quando o motivo for "Outro".
 *
 * Acao reversivel: o lead sai do board ativo e vai para a aba "Perdidos"
 * (pode ser reativado depois). Por isso o botao e `danger` mas nao destrutivo.
 */
import React, { useEffect, useState } from "react";
import { FiAlertCircle, FiXCircle } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import {
  MOTIVO_PERDA_OUTRO,
  MOTIVOS_PERDA_POR_GRUPO,
} from "../../../services/crm";
import { Field, FormStack, InlineHint, Select, Textarea } from "../styles";
import { WarningMsg } from "../../Orcamentos/styles";

function RegistrarPerdaDialog({ open, onClose, onPerder, subtitle }) {
  const [motivo, setMotivo] = useState("");
  const [detalhe, setDetalhe] = useState("");
  const [aviso, setAviso] = useState(null);

  useEffect(() => {
    if (open) {
      setMotivo("");
      setDetalhe("");
      setAviso(null);
    }
  }, [open]);

  const fechar = () => {
    setMotivo("");
    setDetalhe("");
    setAviso(null);
    onClose();
  };

  const detalheObrigatorio = motivo === MOTIVO_PERDA_OUTRO;

  const registrar = async () => {
    setAviso(null);
    if (!motivo) {
      setAviso("Escolha o motivo da perda.");
      throw new Error("Motivo obrigatório.");
    }
    if (detalheObrigatorio && !detalhe.trim()) {
      setAviso("Descreva o motivo no campo de detalhes (motivo 'Outro').");
      throw new Error("Detalhe obrigatório.");
    }
    try {
      await onPerder({ motivo, detalhe: detalhe.trim() });
      fechar();
    } catch (err) {
      setAviso(err?.message || "Não foi possível registrar a perda.");
      throw err;
    }
  };

  return (
    <SlideOverPanel
      open={open}
      onClose={fechar}
      title="Registrar perda do lead"
      subtitle={subtitle || ""}
      width="min(95vw, 480px)"
      footer={
        <>
          <button type="button" className="btn btn--secondary" onClick={fechar}>
            Cancelar
          </button>
          <AsyncButton
            onClick={registrar}
            variant="danger"
            silentSuccess
            silentError
          >
            <FiXCircle size={14} aria-hidden="true" /> Registrar perda
          </AsyncButton>
        </>
      }
    >
      <FormStack>
        <Field>
          Motivo da perda
          <Select
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            autoFocus
          >
            <option value="">Selecione o motivo…</option>
            {MOTIVOS_PERDA_POR_GRUPO.map((g) => (
              <optgroup key={g.grupo} label={g.grupo}>
                {g.itens.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>

        <Field>
          Detalhes {detalheObrigatorio ? "(obrigatório)" : "(opcional)"}
          <Textarea
            value={detalhe}
            onChange={(e) => setDetalhe(e.target.value)}
            rows={4}
            placeholder="Conte o que aconteceu: contexto, concorrente, valor proposto…"
          />
          <InlineHint>
            Anote tudo que ajudar a entender a perda. Esses detalhes alimentam o
            painel &quot;Por que perdemos&quot;.
          </InlineHint>
        </Field>

        {aviso && (
          <WarningMsg>
            <FiAlertCircle size={14} aria-hidden="true" /> {aviso}
          </WarningMsg>
        )}
      </FormStack>
    </SlideOverPanel>
  );
}

export default RegistrarPerdaDialog;
